import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Fade,
  Chip,
  CircularProgress,
  Tooltip,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Typography,
  Button,
  Alert,
  Dialog,
  LinearProgress,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  LocalShipping as LocalShippingIcon,
  Search as SearchIcon,
  History as HistoryIcon,
  FilePresent as FilePresentIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  Info as InfoIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useTheme } from '@mui/material/styles';
import { v4 as uuidv4 } from 'uuid';
import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';
import API from '@Services/apiAxios';
import axios from 'axios';

// ==================== TYPES ====================
interface BotMessage {
  id: string;
  role: 'user' | 'bot';
  content: string;
  timestamp: Date;
  state?: 'complete' | 'awaiting_input' | 'awaiting_confirmation' | 'prefill_ui';
  uiAction?: {
    editUrl: string;
    sections?: {
      consignee?: any;
      shipper?: any;
      package?: any;
    };
  };
  relatedChips?: Array<{
    id: string;
    label: string;
    query: string;
  }>;
  attachmentPreviews?: Array<{ name: string; size: string; type: string }>;
  s3Key?: string;
}

interface ApiResponse {
  message: string;
  state: 'complete' | 'awaiting_input' | 'awaiting_confirmation' | 'prefill_ui';
  uiAction?: {
    editUrl: string;
    sections?: any;
  };
  relatedChips?: Array<{
    id: string;
    label: string;
    query: string;
  }>;
}

interface ShipmentChatBotProps {
  orgCode?: string;
  botEnabled?: boolean;
  onNavigate?: (url: string) => void;
  apiBaseUrl?: string;
}

// ==================== QUICK OPTIONS ====================
const SHIPMENT_QUICK_OPTIONS = [
  {
    id: 'track-shipment',
    label: 'Track Shipment',
    icon: <LocalShippingIcon sx={{ fontSize: 20 }} />,
    query: 'Can you help me track my shipment?',
    category: 'track',
  },
  {
    id: 'check-status',
    label: 'Check Status',
    icon: <SearchIcon sx={{ fontSize: 20 }} />,
    query: 'What is the current status of my shipment?',
    category: 'status',
  },
  {
    id: 'delivery-estimate',
    label: 'Delivery Estimate',
    icon: <InfoIcon sx={{ fontSize: 20 }} />,
    query: 'When will my shipment be delivered?',
    category: 'info',
  },
  {
    id: 'upload-docs',
    label: 'Upload Documents',
    icon: <FilePresentIcon sx={{ fontSize: 20 }} />,
    query: 'I need to upload shipping documents.',
    category: 'info',
  },
];

// ==================== GLASS MORPHISM STYLES ====================
const getGlassStyle = (theme: any) => ({
  backdropFilter: 'blur(20px) saturate(180%)',
  backgroundColor: `rgba(${theme.palette.mode === 'dark' ? '30, 30, 46' : '255, 255, 255'}, 0.6)`,
  border: `1px solid rgba(${theme.palette.mode === 'dark' ? '255, 255, 255' : '0, 0, 0'}, 0.18)`,
  boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0, 0, 0, 0.3)' : '0 8px 32px rgba(0, 0, 0, 0.1)',
});

const getMessageGlassStyle = (theme: any, isUser: boolean) => ({
  backdropFilter: 'blur(15px) saturate(180%)',
  backgroundColor: isUser
    ? `rgba(${theme.palette.primary.main}, 0.15)`
    : `rgba(${theme.palette.mode === 'dark' ? '100, 100, 120' : '200, 200, 220'}, 0.1)`,
  border: `1px solid rgba(${theme.palette.mode === 'dark' ? '255, 255, 255' : '0, 0, 0'}, 0.15)`,
});

// ==================== MAIN COMPONENT ====================
const SpeedboxChatBot: React.FC<ShipmentChatBotProps> = ({
  orgCode = 'DEFAULT-ORG',
  botEnabled = true,
  onNavigate,
  apiBaseUrl,
}) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<BotMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showFileDialog, setShowFileDialog] = useState(false);
  const userName = useSelector(
    (state: RootStateType) =>
      state.loginUser.profile?.name || state.loginUser.profile?.email || state.loginUser.profile?.username || 'User',
  );

  // Bot-specific state
  const [sessionId, setSessionId] = useState('');
  const [currentBotState, setCurrentBotState] = useState<string | null>(null);
  const [conversationHistory, setConversationHistory] = useState<BotMessage[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<any>(null);

  console.log(setUploadProgress);

  // ==================== INITIALIZATION ====================
  useEffect(() => {
    // Generate or retrieve sessionId
    if (!sessionId && isOpen) {
      const savedSessionId = sessionStorage.getItem('botSessionId') || uuidv4();
      sessionStorage.setItem('botSessionId', savedSessionId);
      setSessionId(savedSessionId);

      console.group('🤖 BOT INITIALIZATION');
      console.info('📌 Session ID Generated:', savedSessionId);
      console.info('🏢 Organization Code:', orgCode);
      console.info('⏰ Timestamp:', new Date().toISOString());
      console.info('🔗 API Base URL:', apiBaseUrl);
      console.groupEnd();
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [isOpen, orgCode, apiBaseUrl, sessionId]);

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!botEnabled) {
    return null;
  }

  // ==================== FILE HANDLING ====================
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    // Validate files
    for (const file of files) {
      // Size check
      if (file.size > 10 * 1024 * 1024) {
        setApiError('File size must be less than 10MB');
        return;
      }

      // Type check
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ];

      if (!allowedTypes.includes(file.type)) {
        setApiError('Only PDF, JPG, PNG, Word, and Excel files are allowed');
        return;
      }
    }

    setAttachedFiles(prev => [...prev, ...files]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    console.info(
      '📎 Files selected:',
      files.map(f => ({
        name: f.name,
        size: formatFileSize(f.size),
        type: f.type,
      })),
    );
  }, []);

  const removeFile = useCallback((fileName: string) => {
    setAttachedFiles(prev => prev.filter(file => file.name !== fileName));
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // ==================== FILE UPLOAD TO S3 (Phase 2) ====================
  const uploadFileToS3 = async (file: File): Promise<string | null> => {
    try {
      console.group('📁 FILE UPLOAD - GET PRESIGNED URL');
      console.info('File Name:', file.name);
      console.info('File Size:', (file.size / 1024 / 1024).toFixed(2), 'MB');
      console.info('File Type:', file.type);

      const { data } = await API.post('/bot/upload-url', {
        filename: file.name,
        mimeType: file.type,
      });

      const { uploadUrl, s3Key } = data;
      console.info('📍 S3 Key:', s3Key);
      console.info('🔗 Upload URL received');

      // Upload file to S3
      console.group('📤 UPLOADING TO S3');
      await axios.put(uploadUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
        onUploadProgress: progressEvent => {
          const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));

          setUploadProgress(percent);
        },
      });

      console.info('✅ File uploaded successfully to S3');
      console.info('🔑 S3 Key for API:', s3Key);
      console.groupEnd();
      console.groupEnd();

      return s3Key;
    } catch (error) {
      console.error('❌ File upload error:', error);
      setApiError(`File upload failed: ${error}`);
      return null;
    }
  };

  // ==================== MESSAGE HANDLING ====================
  const handleQuickOption = useCallback((option: any) => {
    setInputValue(option.query);
    setTimeout(() => handleSendMessage(option.query), 100);
  }, []);

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || inputValue.trim();

    if (!textToSend && attachedFiles.length === 0) return;

    try {
      setIsLoading(true);
      setApiError(null);

      let s3Key: string | undefined;

      // Handle file upload
      if (attachedFiles.length > 0) {
        setShowFileDialog(true);
        for (const file of attachedFiles) {
          const key = await uploadFileToS3(file);
          if (key) {
            s3Key = key;
          }
        }
        setShowFileDialog(false);
      }

      // Create user message
      const userMessage: BotMessage = {
        id: uuidv4(),
        role: 'user',
        content: textToSend,
        timestamp: new Date(),
        attachmentPreviews:
          attachedFiles.length > 0
            ? attachedFiles.map(file => ({
                name: file.name,
                size: formatFileSize(file.size),
                type: file.type,
              }))
            : undefined,
      };

      setMessages(prev => [...prev, userMessage]);
      setConversationHistory(prev => [...prev, userMessage]);
      setInputValue('');
      setAttachedFiles([]);

      // ==================== API CALL ====================
      console.group('🤖 BOT API REQUEST');
      const apiPayload = {
        message: textToSend,
        sessionId: sessionId || uuidv4(),
        orgCode: orgCode,
        ...(s3Key && { s3Key }),
      };

      console.info('📤 Request Body:', apiPayload);
      console.info('⏰ Sent at:', new Date().toISOString());
      if (s3Key) {
        console.info('📎 S3 Key:', s3Key);
      }
      console.groupEnd();

      const { data } = await API.post<ApiResponse>('/bot/message', apiPayload);

      console.group('🤖 BOT API RESPONSE');
      console.info('📥 Response:', data);
      console.info('🔄 New State:', data.state);
      if (data.uiAction) {
        console.info('🔗 Edit URL:', data.uiAction.editUrl);
      }
      if (data.relatedChips) {
        console.info('🏷️ Related Chips:', data.relatedChips.length);
      }
      console.groupEnd();

      // Create bot message
      const botMessage: BotMessage = {
        id: uuidv4(),
        role: 'bot',
        content: data.message,
        timestamp: new Date(),
        state: data.state,
        uiAction: data.uiAction,
        relatedChips: data.relatedChips,
      };

      setMessages(prev => [...prev, botMessage]);
      setConversationHistory(prev => [...prev, botMessage]);
      setCurrentBotState(data.state);

      // Auto-navigate on prefill_ui (Phase 2)
      if (data.state === 'prefill_ui' && onNavigate && data.uiAction) {
        console.info('🔗 NAVIGATING TO FORM:', data.uiAction.editUrl);
        onNavigate(data.uiAction.editUrl);
      }
    } catch (error: any) {
      console.group('❌ BOT API ERROR');
      const errorMsg =
        error?.response?.data?.message || error?.response?.data?.msg || error?.message || 'Something went wrong';

      console.groupEnd();

      setApiError(`Failed to send message: ${errorMsg}`);

      const errorMessage: BotMessage = {
        id: uuidv4(),
        role: 'bot',
        content: `⚠️ Error: ${errorMsg}. Please try again.`,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBotStateAction = (action: 'yes' | 'no' | 'open-form', url?: string) => {
    if (action === 'open-form' && url) {
      console.info('🔗 NAVIGATING:', url);
      if (onNavigate) {
        onNavigate(url);
      } else {
        window.location.href = url;
      }
    } else {
      const message = action === 'yes' ? 'Yes' : action === 'no' ? 'No' : '';
      console.info('✅ User Action:', message);
      handleSendMessage(message);
    }
  };

  const handleRelatedChipClick = (chip: any) => {
    console.info('🏷️ Related chip clicked:', chip.label);
    setInputValue(chip.query);
    setTimeout(() => handleSendMessage(chip.query), 100);
  };

  const getUserInitials = () => {
    if (userName && userName !== 'User') {
      return userName.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // ==================== STATE RENDER HELPERS ====================
  const renderBotStateUI = (message: BotMessage) => {
    switch (message.state) {
      case 'complete':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <CheckCircleIcon sx={{ fontSize: 18, color: 'green' }} />
            <Typography variant="caption">Conversation complete</Typography>
          </Box>
        );

      case 'awaiting_input':
        return (
          <Typography variant="caption" sx={{ color: theme.palette.primary.main, mt: 1 }}>
            ↳ Waiting for your response...
          </Typography>
        );

      case 'awaiting_confirmation':
        return (
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button variant="outlined" size="small" onClick={() => handleBotStateAction('yes')} disabled={isLoading}>
              Yes
            </Button>
            <Button variant="outlined" size="small" onClick={() => handleBotStateAction('no')} disabled={isLoading}>
              No
            </Button>
          </Box>
        );

      case 'prefill_ui':
        return message.uiAction ? (
          <Box sx={{ mt: 1 }}>
            <Button
              variant="contained"
              size="small"
              endIcon={<OpenInNewIcon />}
              onClick={() => handleBotStateAction('open-form', message.uiAction!.editUrl)}
              sx={{ mb: 1 }}
            >
              Open Form
            </Button>
            <Typography variant="caption" sx={{ display: 'block', color: 'gray' }}>
              Form will be pre-filled with suggested data
            </Typography>
          </Box>
        ) : null;

      default:
        return null;
    }
  };

  const renderRelatedChips = (chips?: BotMessage['relatedChips']) => {
    if (!chips || chips.length === 0) return null;

    return (
      <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ width: '100%' }}>
          Related topics:
        </Typography>
        {chips.map(chip => (
          <Chip
            key={chip.id}
            label={chip.label}
            size="small"
            onClick={() => handleRelatedChipClick(chip)}
            variant="outlined"
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          />
        ))}
      </Box>
    );
  };

  // ==================== RENDER ====================
  const hasMessages = messages.length > 0;

  return (
    <>
      {/* FLOATING BOT BUTTON */}
      <Tooltip title={isOpen ? 'Close Chat' : 'Open Bot Assistant'}>
        <Badge
          badgeContent={!isOpen && hasMessages ? messages.length : 0}
          color="primary"
          sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 998 }}
        >
          <Box
            onClick={() => setIsOpen(!isOpen)}
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.1)',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
              },
              color: 'white',
              fontSize: 28,
            }}
          >
            🤖
          </Box>
        </Badge>
      </Tooltip>

      {/* CHAT WINDOW */}
      <Fade in={isOpen} unmountOnExit>
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            bottom: 90,
            right: 20,
            width: { xs: 'calc(100% - 40px)', sm: 420 },
            height: { xs: '60vh', sm: '600px' },
            maxHeight: '65vh',
            ...getGlassStyle(theme),
            borderRadius: '20px',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 999,
            overflow: 'hidden',
          }}
        >
          {/* HEADER */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 2,
              borderBottom: `1px solid rgba(0, 0, 0, 0.1)`,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  color: 'white',
                }}
              >
                🤖
              </Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Speedbox Bot
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  {currentBotState ? `State: ${currentBotState}` : 'Ready to help'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Conversation History">
                <IconButton
                  size="small"
                  onClick={() => setShowSidebar(true)}
                  sx={{ color: theme.palette.primary.main }}
                >
                  <HistoryIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton size="small" onClick={() => setIsOpen(false)} sx={{ color: 'inherit' }}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* ERROR ALERT */}
          {apiError && (
            <Alert
              severity="error"
              onClose={() => setApiError(null)}
              sx={{ margin: 1, borderRadius: 2 }}
              icon={<ErrorOutlineIcon />}
            >
              {apiError}
            </Alert>
          )}

          {/* MESSAGES AREA */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              padding: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '10px',
              },
            }}
          >
            {!hasMessages ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  gap: 2,
                  textAlign: 'center',
                  opacity: 0.7,
                }}
              >
                <LocalShippingIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />
                <Typography variant="body2">👋 Hi! I'm Speedbox Bot</Typography>
                <Typography variant="caption">I can help you with shipment management, tracking, and more.</Typography>
                <Typography variant="caption">Select an option below or ask me anything</Typography>
              </Box>
            ) : (
              messages.map(message => (
                <Fade key={message.id} in>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                      gap: 1,
                    }}
                  >
                    {message.role === 'bot' && (
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                          flexShrink: 0,
                        }}
                      >
                        <SmartToyIcon sx={{ fontSize: 14 }} />
                      </Avatar>
                    )}
                    <Box
                      sx={{
                        maxWidth: '75%',
                        ...getMessageGlassStyle(theme, message.role === 'user'),
                        padding: '10px 14px',
                        borderRadius: '12px',
                        wordBreak: 'break-word',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.primary,
                          lineHeight: 1.5,
                        }}
                      >
                        {message.content}
                      </Typography>

                      {/* ATTACHMENTS */}
                      {message.attachmentPreviews && message.attachmentPreviews.length > 0 && (
                        <Box sx={{ marginTop: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {message.attachmentPreviews.map((file, idx) => (
                            <Chip
                              key={idx}
                              icon={<FilePresentIcon />}
                              label={`${file.name.substring(0, 15)}...`}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: '0.7rem',
                                height: 'auto',
                                padding: '4px 8px',
                              }}
                            />
                          ))}
                        </Box>
                      )}

                      {/* BOT STATE UI */}
                      {message.role === 'bot' && (
                        <>
                          {renderBotStateUI(message)}
                          {renderRelatedChips(message.relatedChips)}
                        </>
                      )}

                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          marginTop: 0.5,
                          opacity: 0.6,
                          fontSize: '0.65rem',
                        }}
                      >
                        {message.timestamp.toLocaleTimeString()}
                      </Typography>
                    </Box>
                  </Box>
                </Fade>
              ))
            )}
            {isLoading && (
              <Fade in>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    gap: 1,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    }}
                  >
                    <SmartToyIcon sx={{ fontSize: 14 }} />
                  </Avatar>
                  <Box
                    sx={{
                      ...getMessageGlassStyle(theme, false),
                      padding: '10px 14px',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <CircularProgress size={16} />
                    <Typography variant="caption">Thinking...</Typography>
                  </Box>
                </Box>
              </Fade>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* QUICK OPTIONS */}
          {!hasMessages && (
            <Box
              sx={{
                padding: 1.5,
                borderTop: `1px solid rgba(0, 0, 0, 0.1)`,
                display: 'flex',
                gap: 0.8,
                overflowX: 'auto',
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}
            >
              {SHIPMENT_QUICK_OPTIONS.map(option => (
                <Tooltip key={option.id} title={option.query}>
                  <Chip
                    icon={option.icon}
                    label={option.label}
                    onClick={() => handleQuickOption(option)}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`,
                      border: `1px solid ${theme.palette.primary.main}40`,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontSize: '0.8rem',
                      height: 'auto',
                      padding: '6px 0',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}40, ${theme.palette.secondary.main}40)`,
                        transform: 'translateY(-2px)',
                      },
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          )}

          {/* ATTACHED FILES */}
          {attachedFiles.length > 0 && (
            <Box
              sx={{
                padding: 1,
                borderTop: `1px solid rgba(0, 0, 0, 0.1)`,
                display: 'flex',
                gap: 0.8,
                overflowX: 'auto',
                backgroundColor: `rgba(0, 0, 0, 0.02)`,
              }}
            >
              {attachedFiles.map((file, idx) => (
                <Chip
                  key={idx}
                  icon={<CheckCircleIcon sx={{ color: 'green' }} />}
                  label={`${file.name.substring(0, 20)}...`}
                  onDelete={() => removeFile(file.name)}
                  variant="outlined"
                  size="small"
                  sx={{ fontSize: '0.75rem' }}
                />
              ))}
            </Box>
          )}

          {/* INPUT AREA */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              padding: 1.5,
              borderTop: `1px solid rgba(0, 0, 0, 0.1)`,
              backgroundColor: `rgba(0, 0, 0, 0.02)`,
            }}
          >
            <Tooltip title="Attach File">
              <IconButton
                size="small"
                component="label"
                disabled={isLoading}
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)' },
                }}
              >
                <AttachFileIcon />
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  multiple
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.xlsx,.csv,.jpg,.png"
                />
              </IconButton>
            </Tooltip>

            <TextField
              fullWidth
              size="small"
              placeholder="Ask about your shipments..."
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyPress={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={isLoading}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '0.9rem',
                },
              }}
            />

            <Tooltip title="Send">
              <IconButton
                onClick={() => handleSendMessage()}
                disabled={isLoading || (!inputValue.trim() && attachedFiles.length === 0)}
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)' },
                }}
              >
                <SendIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>
      </Fade>

      {/* SIDEBAR - CONVERSATION HISTORY */}
      <Drawer anchor="right" open={showSidebar} onClose={() => setShowSidebar(false)}>
        <Box sx={{ width: 300, padding: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 2,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Conversation History
            </Typography>
            <IconButton size="small" onClick={() => setShowSidebar(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ marginBottom: 2 }} />
          <List>
            {conversationHistory.length === 0 ? (
              <Typography variant="caption" sx={{ opacity: 0.6 }}>
                No messages yet
              </Typography>
            ) : (
              conversationHistory.map(msg => (
                <ListItem
                  key={msg.id}
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    marginBottom: 1,
                    padding: 1,
                    backgroundColor: msg.role === 'user' ? `${theme.palette.primary.main}15` : 'transparent',
                    borderRadius: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      width: '100%',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {msg.role === 'user' ? (
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: 'primary.main',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: 600,
                          }}
                        >
                          {getUserInitials()}
                        </Avatar>
                      ) : (
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                          }}
                        >
                          <SmartToyIcon sx={{ fontSize: 12 }} />
                        </Avatar>
                      )}
                    </ListItemIcon>
                    <Box sx={{ flex: 1 }}>
                      <ListItemText
                        primary={msg.content.substring(0, 50) + '...'}
                        secondary={msg.timestamp.toLocaleTimeString()}
                        primaryTypographyProps={{ variant: 'caption' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </Box>
                  </Box>
                </ListItem>
              ))
            )}
          </List>
        </Box>
      </Drawer>

      {/* FILE UPLOAD DIALOG */}
      <Dialog open={showFileDialog} onClose={() => setShowFileDialog(false)}>
        <Box sx={{ p: 2, minWidth: 300 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Uploading file to S3...
          </Typography>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
            {uploadProgress}% Complete
          </Typography>
        </Box>
      </Dialog>
    </>
  );
};

export default SpeedboxChatBot;
